import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ShieldCheck, Loader2, CheckCircle2, XCircle } from "lucide-react";
import { UnidadeInput } from "@/components/UnidadeInput";
import { PaisInput } from "@/components/PaisInput";
import { InstituicaoInput } from "@/components/InstituicaoInput";
import { GrauInput } from "@/components/GrauInput";

const empty = () => ({ nome: "", nome_guerra: "", identificacao: "", rg: "", matricula: "", grau_nome: "", instituicao_nome: "", unidade_nome: "", pais_nome: "Brasil", telefone: "", email: "" });

export default function Inscricao() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [form, setForm] = useState(empty());
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get(`/public/inscricao/${token}`).then((r) => setInfo(r.data)).catch(() => setInfo({ valido: false, motivo: "Link inválido" }));
  }, [token]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const uni = (form.unidade_nome || "").trim();
      const uOpts = (info.listas && info.listas.unidade) || [];
      const match = uOpts.find((u) => (u.nome || "").toLowerCase() === uni.toLowerCase());
      const paisNome = (form.pais_nome || "").trim();
      const pOpts = (info.listas && info.listas.pais) || [];
      const paisMatch = pOpts.find((p) => (p.nome || "").toLowerCase() === paisNome.toLowerCase());
      const instNome = (form.instituicao_nome || "").trim();
      const iOpts = (info.listas && info.listas.instituicao) || [];
      const instMatch = iOpts.find((it) => (it.nome || "").toLowerCase() === instNome.toLowerCase());
      const grauNome = (form.grau_nome || "").trim();
      const gOpts = (info.listas && info.listas.grau) || [];
      const grauMatch = gOpts.find((g) => (g.nome || "").toLowerCase() === grauNome.toLowerCase());
      await api.post(`/public/inscricao/${token}`, {
        ...form,
        grau_id: grauMatch ? grauMatch.id : null, grau_nome: grauNome || null,
        instituicao_nome: instNome || null, instituicao_id: instMatch ? instMatch.id : null,
        unidade_nome: uni || null, unidade_id: match ? match.id : null,
        pais_nome: paisNome || null, pais_id: paisMatch ? paisMatch.id : null,
      });
      setDone(true);
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || "Falha ao enviar inscrição");
    } finally { setLoading(false); }
  };

  if (!info) return <div className="min-h-screen flex items-center justify-center bg-background"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  if (!info.valido) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <div className="text-center max-w-sm" data-testid="inscricao-invalida">
          <div className="p-3 rounded-xl bg-red-500/15 border border-red-500/30 mb-4 inline-flex"><XCircle className="h-10 w-10 text-red-400" /></div>
          <h1 className="font-heading text-2xl font-extrabold">Inscrição indisponível</h1>
          <p className="text-sm text-muted-foreground mt-3">{info.motivo || "Este link não está mais disponível."}</p>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <div className="text-center max-w-sm" data-testid="inscricao-sucesso">
          <div className="p-3 rounded-xl bg-emerald-500/15 border border-emerald-500/30 mb-4 inline-flex"><CheckCircle2 className="h-10 w-10 text-emerald-400" /></div>
          <h1 className="font-heading text-2xl font-extrabold">Inscrição enviada!</h1>
          <p className="text-sm text-muted-foreground mt-3">Seus dados foram recebidos e estão <strong>pendentes de validação</strong> pelo instrutor da turma.</p>
        </div>
      </div>
    );
  }

  const listas = info.listas || {};
  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 py-8">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-5 text-center">
          <div className="p-3 rounded-xl bg-primary/15 border border-primary/30 mb-3"><ShieldCheck className="h-8 w-8 text-primary" /></div>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight">Inscrição na Turma</h1>
          <p className="text-sm text-muted-foreground mt-1">{info.curso_nome} · {info.turma_nome}</p>
        </div>
        <form onSubmit={submit} className="space-y-3 bg-card border border-border rounded-xl p-5">
          <div className="space-y-1.5"><Label>Nome completo *</Label><Input data-testid="ins-nome" value={form.nome} onChange={(e) => set({ nome: e.target.value })} required /></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Nome de guerra *</Label><Input data-testid="ins-guerra" value={form.nome_guerra} onChange={(e) => set({ nome_guerra: e.target.value })} required /></div>
            <div className="space-y-1.5"><Label>Identificação funcional *</Label><Input data-testid="ins-identificacao" value={form.identificacao} onChange={(e) => set({ identificacao: e.target.value })} required /></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>RG</Label><Input data-testid="ins-rg" value={form.rg} onChange={(e) => set({ rg: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Matrícula</Label><Input data-testid="ins-matricula" value={form.matricula} onChange={(e) => set({ matricula: e.target.value })} /></div>
          </div>
          <div className="space-y-1.5">
            <Label>Grau / Posto *</Label>
            <GrauInput value={form.grau_nome} onChange={(v) => set({ grau_nome: v })} opcoes={listas.grau || []} testid="ins-grau" label={null} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <InstituicaoInput
                value={form.instituicao_nome}
                onChange={(v) => set({ instituicao_nome: v })}
                opcoes={listas.instituicao || []}
                testid="ins-instituicao"
              />
            </div>
            <div className="space-y-1.5">
              <UnidadeInput
                value={form.unidade_nome}
                onChange={(v) => set({ unidade_nome: v })}
                opcoes={listas.unidade || []}
                testid="ins-unidade"
                label="Unidade / Org."
              />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <PaisInput
                value={form.pais_nome}
                onChange={(v) => set({ pais_nome: v })}
                opcoes={listas.pais || []}
                testid="ins-pais"
                label="País"
              />
            </div>
            <div className="space-y-1.5"><Label>Telefone</Label><Input data-testid="ins-telefone" value={form.telefone} onChange={(e) => set({ telefone: e.target.value })} /></div>
          </div>
          <div className="space-y-1.5"><Label>E-mail (opcional)</Label><Input data-testid="ins-email" type="email" value={form.email} onChange={(e) => set({ email: e.target.value })} /></div>
          {error && <p data-testid="ins-error" className="text-sm text-red-400">{error}</p>}
          <Button type="submit" data-testid="ins-submit" disabled={loading || !form.nome || !form.nome_guerra || !form.identificacao} className="w-full">
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Enviar inscrição
          </Button>
        </form>
      </div>
    </div>
  );
}
